// src/Helpers/materialLoader.jsx
import * as THREE from "three";
import { texturePreloader } from "../utils/TexturePreloader";
import { withCrossOrigin } from "../utils/ensureTextureCrossOrigin";

export const materialLoader = (profileMat, materialList, listName, envIntensity, profileColor) => {
  const color = materialList.find((color) => color.name === profileMat);
  const newMaterial = new THREE.MeshStandardMaterial();

  if (profileMat === "Regular3") {
    newMaterial.color = new THREE.Color(profileColor);
    newMaterial.roughness = 0.5;
    newMaterial.metalness = 0;
    newMaterial.envMapIntensity = envIntensity;
    newMaterial.side = THREE.DoubleSide;
    newMaterial.shadowSide = THREE.DoubleSide;
    newMaterial.depthWrite = true;
    newMaterial.depthTest = true;
    newMaterial.transparent = false;  
    return newMaterial;
  }

  // Handle textured materials
  if (color.texture) {
    // Try to get from preloader first
    let texture = texturePreloader.getTexture(listName, color.name, 'texture');
    
    // If not in preloader cache, create an immediate texture
    if (!texture) {
      console.log(`Texture not in cache for ${color.name}, creating immediate texture`);
      texture = new THREE.Texture();
      // Start loading but don't wait
      withCrossOrigin(new THREE.TextureLoader()).load(
        color.texture,
        (loadedTexture) => {
          texture.image = loadedTexture.image;
          texture.needsUpdate = true;
          // Apply settings when loaded
          texture.repeat.set(color.size[0], color.size[1]);
          texture.wrapS = THREE.RepeatWrapping;
          texture.wrapT = THREE.RepeatWrapping;
          texture.offset.set(color.offset[0], color.offset[1]);
          texture.rotation = (Math.PI / 2) * color.rotation;
          if (color.space) {
            texture.colorSpace = color.space;
          }
          newMaterial.needsUpdate = true;
        }
      );
    }
    
    newMaterial.map = texture;
  } else {
    newMaterial.color = new THREE.Color("White");
  }

  // Handle other texture types
  const textureTypes = [
    { type: 'roughnessMap', prop: 'roughnessMap', url: color.roughnessMap },
    { type: 'normalMap', prop: 'normalMap', url: color.normalMap },
    { type: 'metalnessMap', prop: 'metalnessMap', url: color.metalnessMap },
    { type: 'aoMap', prop: 'aoMap', url: color.aoMap }
  ];

  for (const { type, prop, url } of textureTypes) {
    if (url) {
      let texture = texturePreloader.getTexture(listName, color.name, type);
      
      if (!texture) {
        texture = new THREE.Texture();
        withCrossOrigin(new THREE.TextureLoader()).load(
          url,
          (loadedTexture) => {
            texture.image = loadedTexture.image;
            texture.needsUpdate = true;
            texture.repeat.set(color.size[0], color.size[1]);
            texture.wrapS = THREE.RepeatWrapping;
            texture.wrapT = THREE.RepeatWrapping;
            texture.offset.set(color.offset[0], color.offset[1]);
            texture.rotation = (Math.PI / 2) * color.rotation;
            newMaterial.needsUpdate = true;
          }
        );
      }
      
      newMaterial[prop] = texture;
    }
  }

  // Rest of your material settings
  newMaterial.roughness = color.roughness;
  newMaterial.metalness = color.metalness;
  newMaterial.envMapIntensity = color.envMapIntensity * envIntensity;
  newMaterial.minFilter = THREE.LinearFilter;
  newMaterial.side = THREE.DoubleSide;

  if (color.emissive) {
    newMaterial.emissive = new THREE.Color("#ffffff");
    newMaterial.emissiveIntensity = 0.75;
    let emissiveTexture = texturePreloader.getTexture(listName, color.name, 'texture');
    
    if (!emissiveTexture) {
      emissiveTexture = new THREE.Texture();
      withCrossOrigin(new THREE.TextureLoader()).load(
        color.texture,
        (loadedTexture) => {
          emissiveTexture.image = loadedTexture.image;
          emissiveTexture.needsUpdate = true;
          emissiveTexture.repeat.set(color.size[0], color.size[1]);
          emissiveTexture.wrapS = THREE.RepeatWrapping;
          emissiveTexture.wrapT = THREE.RepeatWrapping;
          newMaterial.needsUpdate = true;
        }
      );
    }
    
    newMaterial.emissiveMap = emissiveTexture;
    newMaterial.envMapIntensity = 1;
  }

  return newMaterial;
};
